import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { action, get, computed } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import type { SafeString } from '@ember/template/-private/handlebars';
import { waitFor } from '@ember/test-waiters';
import { isPresent } from '@ember/utils';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { findBy } from '@nullvoxpopuli/ember-composable-helpers';
import { call } from '@nullvoxpopuli/ember-composable-helpers';
import {
  addDays,
  addMinutes,
  addMonths,
  addSeconds,
  addYears,
  endOfDay,
  endOfMonth,
  format,
  formatISO,
  fromUnixTime,
  getUnixTime,
  isAfter,
  isPast,
  isSameDay,
  isSameMonth,
  isToday,
  startOfDay,
  startOfMonth,
  subDays,
  subMinutes,
  subMonths,
} from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import can from 'ember-can/helpers/can';
import type AbilitiesService from 'ember-can/services/abilities';
import type { Task } from 'ember-concurrency';
import { all, dropTask, task } from 'ember-concurrency';
import PowerSelect from 'ember-power-select/components/power-select';
import EmberTooltip from 'ember-tooltips/components/ember-tooltip';
import { and, eq, not, notEq, or } from 'ember-truth-helpers';
import CalendarGrid from 'garaje/components/calendar-grid';
import ComboBox from 'garaje/components/combo-box';
import LoadMore from 'garaje/components/load-more';
import UiCard from 'garaje/components/ui/card';
import LoadingSpinnerPulse from 'garaje/components/ui/classic/loading-spinner-pulse';
import UiButton from 'garaje/components/ui-button';
import UiInputField from 'garaje/components/ui-input-field';
import dateFormat from 'garaje/helpers/date-format';
import preventDefault from 'garaje/helpers/prevent-default';
import type Employee from 'garaje/models/employee';
import type EmployeeLocation from 'garaje/models/employee-location';
import type InviteModel from 'garaje/models/invite';
import type ReservationModel from 'garaje/models/reservation';
import type WorkplaceDayModel from 'garaje/models/workplace-day';
import type AjaxService from 'garaje/services/ajax';
import type FeatureConfigService from 'garaje/services/feature-config';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type LiveMapService from 'garaje/services/live-map';
import type { ResourceState } from 'garaje/services/live-map';
import { ResourceType } from 'garaje/services/live-map';
import type StateService from 'garaje/services/state';
import type DayConfig from 'garaje/types/date-time/day-config';
import { SECONDS_IN_A_DAY, GENERIC_RESOURCE_ID_MAPPING } from 'garaje/utils/enums';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import { endTimeOptions, getPartialTimes, hourOptions, startTimeOptions } from 'garaje/utils/hour-options';
import { isNonEmptyArray } from 'garaje/utils/type-utils';
import urlBuilder from 'garaje/utils/url-builder';
import _groupBy from 'lodash/groupBy';
import type moment from 'moment-timezone';
import { localCopy } from 'tracked-toolbox';

interface Option {
  value: string;
  label: string;
}

interface ReservationModalEmployeeDateSelectSignature {
  Args: {
    allDayEnabled: boolean;
    /**
     * Ideally should be named: `isAutoAssignDeskCheckboxChecked`.
     *
     * ### Notes from 2023-07-28
     * * In the context of the Employee Log, false means we don't attempt to assign a desk at all.
     * * In the context of the Desk Reservation tab, false means we will try to show a floor map to pick a desk.
     */
    autoAssignEnabled?: boolean;
    canUsePartialDay?: boolean;
    createDeskReservationTask: Task<unknown, [{ signIn: boolean }]>;
    createResourceReservationTask: Task<unknown, unknown[]>;
    currentReservation: ReservationModel;
    employees: Employee[];
    floorsWithMap: unknown[];
    getSelectedEmployeeTask: Task<unknown, unknown[]>;
    hasMorePages: boolean;
    /**
     * Ideally should be an enum with `parentTab: 'employee-log' | 'desk-reservations'`
     *
     * `isDbeam` true means that we are in the employee-log tab
     */
    isDbeam?: boolean;
    isEditing: boolean;
    isEmployeeLog: boolean;
    loadMoreTask: Task<unknown, unknown[]>;
    multidayEnabled: boolean;
    onAllDayToggleClick: () => void;
    onDateSelect: (date: number | moment.Moment | Date) => void;
    onMultidayToggleClick: () => void;
    onPartialTimeChange: (key: 'startTime' | 'endTime', time: string) => void;
    reservationModalTask: Task<unknown, unknown[]>;
    searchTask: Task<Employee[], [string]>;
    selectedDates: Date[];
    selectedEmployee: Employee;
    onEmployeeSelect: (employee: Employee) => void;
    selectedTime: { startTime: string; endTime: string };
    setCurrentPage: (page: string) => void;
    setSelectedResourceType: (resourceType: ResourceTypeOption) => void;
    selectedResourceType: ResourceTypeOption;
    workplaceDays: WorkplaceDayModel[];
    onAutoAssignDesksClick: () => void;
  };
}

interface ProfileData {
  id: number;
  fullName: string;
  email: string;
  profilePhotoUrl: string;
}

interface ResourceReservationData {
  reservationId: number;
  resourceId: number;
  resourceTypeId: number;
  relationId: string;
  startTime: string;
  endTime: string;
  reservedFor: string;
  reservedForProfileData: ProfileData | null;
  reservedBy: string;
  cancelledAt: string;
  cancelReason: string;
  bookingSource: string;
  reservationCreatedAt: string;
  reservationUpdatedAt: string;
  resourceName: string;
  floorId: number;
}

interface ResourceReservationResponse {
  data: ResourceReservationData[];
}

type ResourceTypeOption = {
  name: string;
  code: string;
  resourceType: ResourceType;
  disabled?: boolean;
};

export default class ReservationModalEmployeeDateSelect extends Component<ReservationModalEmployeeDateSelectSignature> {
  @service declare flashMessages: FlashMessagesService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare abilities: AbilitiesService;
  @service declare state: StateService;
  @service declare store: StoreService;
  @service declare ajax: AjaxService;
  @service declare featureConfig: FeatureConfigService;
  @service declare liveMap: LiveMapService;

  @localCopy('args.employees') declare loadedEmployees: typeof this.args.employees;
  @localCopy('args.workplaceDays') declare workplaceDays: typeof this.args.workplaceDays;

  @tracked showCalendar = false;
  @tracked showingDate = new Date();
  @tracked inviteForToday: InviteModel | null | undefined;
  @tracked availableDesks = [];
  // @ts-expect-error not initialized error
  @tracked employees = this.loadedEmployees;
  @tracked datesToDisplay: (Date | moment.Moment)[] = this.args.selectedDates; // eslint-disable-line ember/no-tracked-properties-from-args
  @tracked deskAvailabilities: { 'start-time'?: string; 'end-time'?: string; availability?: string }[] = [];
  @tracked employeeResourceReservationForMonth: ResourceReservationData[] = [];
  @tracked resourceAvailabilitiesByDate: Record<string, ResourceState[]> = {};
  @tracked private outsideClickListener: ((event: MouseEvent) => void) | null = null;

  get canSignIn(): boolean {
    return Boolean(this.args.isEmployeeLog && !this.args.isEditing);
  }

  get disableAutoDeskAssignment(): boolean {
    return Boolean(this.args.isDbeam && this.args.isEditing && this.args.currentReservation);
  }

  get currentMonth(): string {
    // Return as YYYY-MM-DD string to avoid timezone conversion issues in CalendarGrid
    return format(startOfMonth(this.showingDate), 'yyyy-MM-dd');
  }

  get activeDays(): typeof this.workplaceDays {
    return this.workplaceDays.filter((day) => day.active);
  }

  get selectedForToday(): (typeof this.args.selectedDates)[0] | undefined {
    const { selectedDates } = this.args;
    return selectedDates.find((day) => isToday(subMinutes(day, this.state.minutesBetweenTimezones(day))));
  }

  get selectedDays(): WorkplaceDayModel[] {
    const { selectedDates } = this.args;
    const selectedDays: WorkplaceDayModel[] = [];
    selectedDates.forEach((date) => {
      const currDate = new Date(date);
      const dayOfWeek = format(subMinutes(currDate, this.state.minutesBetweenTimezones(currDate)), 'EEEE');
      const activeDay = this.activeDays.find((day) => day.dayOfWeek === dayOfWeek);
      if (activeDay && !selectedDays.includes(activeDay)) {
        selectedDays.push(activeDay);
      }
    });
    return selectedDays;
  }

  get isInvalidDaySelected(): boolean {
    const { selectedDates } = this.args;

    return selectedDates.some((date) => {
      const currDate = new Date(date);
      const dayOfWeek = format(subMinutes(currDate, this.state.minutesBetweenTimezones(currDate)), 'EEEE');
      const activeDay = this.activeDays.find((day) => day.dayOfWeek === dayOfWeek);
      return !activeDay;
    });
  }

  get latestStartTime(): string {
    const latestStartTime = this.selectedDays.sort((a, b) => (a.startTime.localeCompare(b.startTime) < 0 ? 1 : -1));
    if (this.selectedForToday && latestStartTime.length) {
      const currentTime = format(subMinutes(new Date(), this.state.minutesBetweenTimezones()), 'HH:mm:ss');
      const closestInterval = hourOptions(15, true).find(({ value }) => value.localeCompare(currentTime) >= 0);
      return currentTime.localeCompare(latestStartTime[0]!.startTime) === 1
        ? closestInterval!.value
        : latestStartTime[0]!.startTime;
    }
    return latestStartTime.length ? latestStartTime[0]!.startTime : '00:00:00';
  }

  get earliestEndTime(): string {
    const earliestEndTime = this.selectedDays.sort((a, b) => a.endTime.localeCompare(b.endTime));
    return earliestEndTime.length ? earliestEndTime[0]!.endTime : '23:30:00';
  }

  get startHourOptions(): Option[] {
    const { selectedTime } = this.args;

    const options = startTimeOptions(selectedTime.endTime, 15, true);
    if (this.latestStartTime) {
      return options.filter((option) => {
        return option.value.localeCompare(this.latestStartTime) >= 0;
      });
    }

    return options;
  }

  get endHourOptions(): Option[] {
    const { selectedTime } = this.args;

    const options = endTimeOptions(selectedTime.startTime, 15, true);
    if (!this.earliestEndTime) {
      return options;
    }

    return options.filter((option) => {
      return option.value.localeCompare(this.earliestEndTime) <= 0;
    });
  }

  get daysWithReservations(): { [startDate: string]: ReservationModel[] | ResourceReservationData[] } {
    if (this.isDesksContext) {
      return _groupBy(this.deskReservationsForMonth, (res) => {
        const startDate = fromUnixTime(res.startTime);
        return format(subMinutes(startDate, this.state.minutesBetweenTimezones(startDate)), 'dd-MM-yyyy');
      });
    }

    const resourceReservations = this.employeeResourceReservationForMonth.filter((res) => {
      return (
        res.resourceTypeId ===
        GENERIC_RESOURCE_ID_MAPPING[this.args.selectedResourceType.code as keyof typeof GENERIC_RESOURCE_ID_MAPPING]
      );
    });

    return _groupBy(resourceReservations, (res) => {
      const startDate = new Date(res.startTime);
      return format(subMinutes(startDate, this.state.minutesBetweenTimezones(startDate)), 'dd-MM-yyyy');
    });
  }

  get daysToCustomize(): DayConfig[] {
    const { currentReservation, canUsePartialDay } = this.args;
    if (!this.deskReservationsForMonth && this.isDesksContext) {
      return [];
    }

    if (!this.employeeResourceReservationForMonth && !this.isDesksContext) {
      return [];
    }

    return Object.keys(this.daysWithReservations).map((date) => {
      // using ! to preserve past behavior
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const reservations = this.daysWithReservations[date]!.sort((a, b) => (a.startTime as any) - (b.startTime as any));
      const isCurrentRes = reservations.find((res) => currentReservation?.startTime === res.startTime);
      const bgColor = isCurrentRes ? 'bg-carbon-60 current-res' : 'bg-red-40 res-exists';
      return {
        // Resource reservations are stored as ISOstrings, so we need to convert them to unix timestamps.
        date: this.isDesksContext
          ? <number>reservations[0]!.startTime
          : Math.round(new Date(reservations[0]!.startTime).getTime() / 1000),
        tooltip: canUsePartialDay ? this.tooltipHTML(reservations) : 'Reservation exists',
        class: bgColor + ' rounded-full py-1 px-1.5 text-white',
        disabled: canUsePartialDay || isCurrentRes ? false : true,
      };
    });
  }

  get currentReservationStartTime(): Date {
    return fromUnixTime(this.args.currentReservation?.startTime);
  }

  get isCurrentResSelected(): boolean {
    const { selectedDates, isEditing } = this.args;

    const officeStartTime = subMinutes(
      this.currentReservationStartTime,
      this.state.minutesBetweenTimezones(this.currentReservationStartTime),
    );
    return selectedDates.some(
      (date) => isEditing && isSameDay(officeStartTime, subMinutes(date, this.state.minutesBetweenTimezones(date))),
    );
  }

  get canUseDelegatedParking(): boolean {
    return this.featureFlags.isEnabled('delegated-resource-booking') && this.abilities.can('use parking resource');
  }

  get isDesksContext(): boolean {
    return (
      !this.canUseDelegatedParking ||
      (this.featureFlags.isEnabled('delegated-resource-booking') && this.args.selectedResourceType.code === 'desk')
    );
  }

  get canAutoAssignDesks(): boolean {
    if (!this.featureConfig.isEnabled('deskReservations.autoAssignment') && this.isDesksContext) {
      return false;
    }

    return true;
  }

  get resourceTypeOptions(): ResourceTypeOption[] {
    return [
      { name: 'Desk', code: 'desk', resourceType: ResourceType.DESK },
      {
        name: this.canUseDelegatedParking ? 'Parking' : 'Parking (Coming soon)',
        code: 'parking',
        disabled: !this.canUseDelegatedParking,
        resourceType: ResourceType.PARKING,
      },
    ];
  }

  @action async onResourceTypeSelect(option: ResourceTypeOption): Promise<void> {
    const { selectedDates, selectedEmployee, setSelectedResourceType } = this.args;
    setSelectedResourceType(option);
    if (selectedEmployee) {
      if (isNonEmptyArray(selectedDates)) {
        if (!this.isDesksContext) {
          await this.loadResourceReservationsForMonth.perform();
          await this.checkForAvailableResourceTask.perform();
        }
      }
    }
  }

  tooltipHTML(reservations: ReservationModel[] | ResourceReservationData[]): SafeString {
    const resDetails = reservations.map((res) => {
      const startTime = this.isDesksContext ? fromUnixTime(res.startTime as number) : new Date(res.startTime as string);
      const endTime = this.isDesksContext ? fromUnixTime(res.endTime as number) : new Date(res.endTime as string);
      const resStart = startTime;
      const resEnd = endTime;
      return `<div>${format(resStart, 'h:mma')} to ${format(resEnd, 'h:mma')}</div>`;
    });
    return htmlSafe(`
    <div data-test-day-tooltip>
      <div>Reserved</div>
      ${resDetails.join('')}
    </div>`);
  }

  get reservationExistsOnSelectedDate(): boolean {
    const { selectedDates, currentReservation, isEditing } = this.args;
    if (!this.isDesksContext) {
      const exists = this.employeeResourceReservationForMonth.some((res) => {
        return selectedDates.some((date) => this.checkForResourceReservationConflicts(date, res));
      });
      return exists;
    }
    return this.deskReservationsForMonth.some((res) => {
      if (isEditing && this.isCurrentResSelected && res.id === currentReservation?.id) {
        return false;
      }
      const startTime = fromUnixTime(res.startTime);
      return selectedDates.some((date) =>
        isSameDay(
          subMinutes(startTime, this.state.minutesBetweenTimezones(startTime)),
          subMinutes(date, this.state.minutesBetweenTimezones(date)),
        ),
      );
    });
  }

  get firstSelectedDatesLocationTime(): string | undefined {
    return this.selectedDatesLocationTime[0];
  }

  get selectedDatesLocationTime(): string[] {
    return this.datesToDisplay.map((date) => {
      const currDate = new Date(date as Date);
      return format(subMinutes(currDate, this.state.minutesBetweenTimezones(currDate)), 'MM/dd/yyyy');
    });
  }

  get hasError(): boolean {
    return !!(this.reservationErrors.length || this.partialDayError);
  }

  get deskReservationsForMonth(): ReservationModel[] {
    const { allDayEnabled, selectedTime } = this.args;
    const reservations = this.loadDeskReservationsForMonthTask?.lastSuccessful?.value || [];
    if (allDayEnabled) {
      return reservations;
    }

    const partialReservations = reservations.filter((res) => {
      const resStartTime = fromUnixTime(res.startTime);
      const midnight = this.state.getOfficeLocationTime(
        startOfDay(subMinutes(resStartTime, this.state.minutesBetweenTimezones(resStartTime))),
      );
      const [partialStart, partialEnd] = getPartialTimes(midnight, selectedTime);
      const startTime = getUnixTime(partialStart!);
      const endTime = getUnixTime(partialEnd!);
      return res.startTime < endTime && res.endTime > startTime;
    });
    return partialReservations;
  }

  get dateAvailabilityArray(): { 'start-time': number; 'end-time': number }[] {
    const { selectedDates, allDayEnabled, selectedTime } = this.args;
    return selectedDates.map((date) => {
      const midnight = this.state.getOfficeLocationTime(
        // using ! to preserve past behavior
        startOfDay(toZonedTime(date, this.state.currentLocation.timezone)),
      );
      const [partialStart, partialEnd] = getPartialTimes(midnight, selectedTime);
      const startTime = allDayEnabled ? midnight : partialStart!;
      const endTime = allDayEnabled
        ? // using ! to preserve past behavior
          this.state.getOfficeLocationTime(endOfDay(toZonedTime(date, this.state.currentLocation.timezone)))
        : partialEnd!;
      return { 'start-time': getUnixTime(startTime), 'end-time': getUnixTime(endTime) };
    });
  }

  get nextButtonText(): string {
    const {
      autoAssignEnabled,
      isDbeam,
      createDeskReservationTask,
      createResourceReservationTask,
      isEmployeeLog,
      isEditing,
    } = this.args;
    if (autoAssignEnabled || isDbeam) {
      const saving = createDeskReservationTask.isRunning || createResourceReservationTask.isRunning;

      if (!isEditing && isEmployeeLog) {
        if (saving) return 'Scheduling...';
        return 'Schedule';
      }

      if (isEditing && isEmployeeLog) {
        if (saving) return 'Saving...';
        return 'Save';
      }

      if (saving) return 'Confirming...';
      return 'Confirm';
    }
    return 'Next';
  }

  get disableNextButton(): string | number | boolean {
    const {
      selectedEmployee,
      createDeskReservationTask,
      createResourceReservationTask,
      floorsWithMap,
      autoAssignEnabled,
      isDbeam,
    } = this.args;
    if (isDbeam) {
      return (
        !selectedEmployee ||
        this.hasError ||
        this.onEmployeeSelectTask.isRunning ||
        createDeskReservationTask.isRunning ||
        createResourceReservationTask.isRunning ||
        this.checkForAvailableResourceTask.isRunning
      );
    }

    const alwaysDisabledConditions =
      !selectedEmployee ||
      this.hasError ||
      this.onEmployeeSelectTask.isRunning ||
      createDeskReservationTask.isRunning ||
      createResourceReservationTask.isRunning ||
      (!floorsWithMap.length && !autoAssignEnabled);

    return alwaysDisabledConditions || this.showCalendar || this.checkForAvailableDesksTask.isRunning;
  }

  get startTimePlaceholder(): string | null {
    const { currentReservation } = this.args;
    if (!currentReservation) {
      return null;
    }
    const startDate = this.currentReservationStartTime;
    const officeLocationStart = subMinutes(startDate, this.state.minutesBetweenTimezones(startDate));
    return format(officeLocationStart, 'h:mm aaa');
  }

  get endTimePlaceholder(): string | null {
    const { currentReservation } = this.args;
    if (!currentReservation) {
      return null;
    }
    const endDate = fromUnixTime(currentReservation.endTime);
    const officeLocationEnd = subMinutes(endDate, this.state.minutesBetweenTimezones(endDate));
    return format(officeLocationEnd, 'h:mm aaa');
  }

  @action
  onPartialTimeChange(attr: 'startTime' | 'endTime', option: Option): void {
    const { onPartialTimeChange } = this.args;
    onPartialTimeChange(attr, option.value);
    void this.checkForAvailableDesksTask.perform();
  }

  setPartialTimes(start: string, end: string): void {
    const { onPartialTimeChange } = this.args;
    onPartialTimeChange('startTime', start);
    onPartialTimeChange('endTime', end);
  }

  @action
  onAllDayToggleClick(): void {
    const { onAllDayToggleClick, isEditing, currentReservation } = this.args;
    if (isEditing) {
      const startTime = fromUnixTime(currentReservation.startTime);
      const endTime = fromUnixTime(currentReservation.endTime);
      const formattedStartTime = format(
        subMinutes(startTime, this.state.minutesBetweenTimezones(startTime)),
        'HH:mm:ss',
      );
      const formattedEndTime = format(subMinutes(endTime, this.state.minutesBetweenTimezones(endTime)), 'HH:mm:ss');
      this.setPartialTimes(formattedStartTime, formattedEndTime);
    } else {
      this.setPartialTimes(this.latestStartTime, this.earliestEndTime);
    }
    onAllDayToggleClick();
    void this.loadDeskReservationsForMonthTask.perform();
    void this.checkForAvailableDesksTask.perform();
  }

  @action
  onNextButtonClick(signIn = false): void {
    const {
      autoAssignEnabled,
      selectedResourceType,
      isDbeam,
      createDeskReservationTask,
      createResourceReservationTask,
      setCurrentPage,
    } = this.args;

    if (selectedResourceType && selectedResourceType.code === 'desk') {
      if (autoAssignEnabled || isDbeam) {
        void createDeskReservationTask.perform({ signIn });
      } else {
        setCurrentPage('map');
      }
    } else if (selectedResourceType && selectedResourceType.code === 'parking') {
      if (autoAssignEnabled || isDbeam) {
        void createResourceReservationTask.perform();
      } else {
        setCurrentPage('resources-map');
      }
    }
  }

  @action
  dateHasError(date: Date): boolean {
    const { allDayEnabled, selectedTime, isDbeam, autoAssignEnabled } = this.args;

    if (isDbeam && !autoAssignEnabled) {
      return false;
    }

    const dateTime = new Date(date);
    const locationTime = this.state.getOfficeLocationTime(dateTime);
    const [partialStart, partialEnd] = getPartialTimes(locationTime, selectedTime);

    const startTime = allDayEnabled ? locationTime : partialStart;
    const endTime = allDayEnabled ? this.state.getOfficeLocationTime(endOfDay(dateTime)) : partialEnd;

    if (this.isDesksContext) {
      const hasNoDeskAvailablities = this.deskAvailabilities.some(
        (date) =>
          parseInt(date['start-time']!) === getUnixTime(startTime!) &&
          parseInt(date['end-time']!) === getUnixTime(endTime!) &&
          date.availability === 'false',
      );

      const hasConflictingDeskReservation = this.deskReservationsForMonth.some((res) => {
        const startTime = fromUnixTime(res.startTime);
        return isSameDay(subMinutes(startTime, this.state.minutesBetweenTimezones(startTime)), dateTime);
      });

      return hasNoDeskAvailablities || hasConflictingDeskReservation;
    } else {
      const hasNoResourceAvailablities = this.resourceAvailabilitiesByDate[locationTime.toISOString()]?.length === 0;
      const hasConflictingResourceReservation = this.dateHasResourceReservationConflict(dateTime);
      return hasConflictingResourceReservation || hasNoResourceAvailablities;
    }
  }

  dateHasResourceReservationConflict(date: Date): boolean {
    return this.employeeResourceReservationForMonth.some((res) => this.checkForResourceReservationConflicts(date, res));
  }

  checkForResourceReservationConflicts(date: Date, res: ResourceReservationData): boolean {
    const startTime = new Date(res.startTime);
    return (
      isSameDay(
        subMinutes(startTime, this.state.minutesBetweenTimezones(startTime)),
        subMinutes(date, this.state.minutesBetweenTimezones(date)),
      ) &&
      res.resourceTypeId ===
        GENERIC_RESOURCE_ID_MAPPING[this.args.selectedResourceType?.code as keyof typeof GENERIC_RESOURCE_ID_MAPPING] &&
      !res.cancelledAt
    );
  }

  @action
  reasonEmployeeCannotReserve(employee?: Employee): string | undefined {
    // eslint-disable-next-line ember/no-get
    if (!isPresent(get(employee, 'user.id'))) {
      return 'No account exists';
    }

    if (this.featureFlags.isEnabled('protect-scheduling-limits') && this.hasNoApprovedDocument(employee!)) {
      return 'No approved document';
    }

    return;
  }

  @action
  onMultiSelectClick(): void {
    const { onMultidayToggleClick } = this.args;
    onMultidayToggleClick();
    if (this.isDesksContext) {
      void this.checkForAvailableDesksTask.perform();
    } else {
      void this.checkForAvailableResourceTask.perform();
    }
  }

  checkForAvailableDesksTask = task({ restartable: true }, async () => {
    if (!this.isValidBookingHours || !this.dateAvailabilityArray.length) {
      return;
    }

    const url = urlBuilder.rms.getDeskAvailability();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
    const response = await this.ajax.request<any>(url, {
      type: 'POST',
      contentType: 'application/vnd.api+json',
      data: JSON.stringify({
        email: this.args.selectedEmployee.email,
        'location-id': this.state.currentLocation.id,
        'availability-ranges': this.dateAvailabilityArray,
      }),
    });
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    this.deskAvailabilities = response.availabilities;
  });

  checkForAvailableResourceTask = task({ restartable: true }, async () => {
    const resourceAvailabilityForSelectedDatesPromises = this.dateAvailabilityArray.map((dateAvailability) => {
      const queryFilters = {
        'location-id': this.state.currentLocation.id,
        'start-time': new Date(dateAvailability['start-time'] * 1000).toISOString(),
        'end-time': new Date(dateAvailability['end-time'] * 1000).toISOString(),
        'resource-type-ids': '2',
        'employee-id': this.args.selectedEmployee.id,
      };
      const url = urlBuilder.rms.getResourceAvailability(queryFilters);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
      const response = this.ajax.request<any>(url, {
        type: 'GET',
        contentType: 'application/vnd.api+json',
      });
      return response;
    });

    type ResourceAvailabilityResponse = {
      data: [];
    };

    const resourceAvailabilityForSelectedDates = (await all(
      resourceAvailabilityForSelectedDatesPromises,
    )) as ResourceAvailabilityResponse[];

    for (let idx = 0; idx < resourceAvailabilityForSelectedDates.length; idx++) {
      const resourceAvailabilityForDay = resourceAvailabilityForSelectedDates[idx];
      const date = this.args.selectedDates[idx];
      if (resourceAvailabilityForDay) {
        const resourceAvailabilities = resourceAvailabilityForDay.data ?? [];
        if (date) {
          this.resourceAvailabilitiesByDate = {
            ...this.resourceAvailabilitiesByDate,
            [date.toISOString()]: resourceAvailabilities,
          };
        }
      }
    }
  });

  loadInviteForTodayTask = task({ drop: true }, async () => {
    try {
      const filter = {
        locationId: this.state.currentLocation?.id,
        dateTo: formatISO(addSeconds(this.state.getOfficeLocationTime(startOfDay(new Date())), SECONDS_IN_A_DAY - 1)),
        dateFrom: formatISO(this.state.getOfficeLocationTime(startOfDay(new Date()))),
        email: encodeURIComponent(this.args.selectedEmployee.email),
      };
      const url = urlBuilder.v3.invites.fetchEmployeeInviteOnDate(filter);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
      const response = await this.ajax.request<any>(url, {
        type: 'GET',
        contentType: 'application/vnd.api+json',
        headers: { accept: 'application/vnd.api+json' },
      });
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const invite = response.data.firstObject;
      if (invite) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        this.inviteForToday = await this.store.findRecord('invite', invite.id);
      } else {
        this.inviteForToday = null;
      }
    } catch (e) {
      this.flashMessages.showFlash('error', 'Error fetching invite for today', parseErrorForDisplay(e));
      throw e;
    }
  });

  get dateLimits(): { startDate?: number; endDate?: number } {
    const { selectedEmployee } = this.args;
    const limits: { startDate?: number; endDate?: number } = {};
    const endOfRegistration = addMinutes(
      startOfDay(new Date()),
      this.state.currentLocation.registrationEligibilityEndOffset,
    );

    const locationDate = this.state.getOfficeLocationTime(startOfDay(new Date()));

    if (!this.inviteForToday?.preregistrationComplete && isAfter(locationDate, endOfRegistration)) {
      limits['startDate'] = getUnixTime(addDays(locationDate, 1));
    } else {
      limits['startDate'] = getUnixTime(locationDate);
    }

    const employeeExpiration = selectedEmployee?.employeeLocations.content[0]!.requiredDocumentApprovalExpiresAt;

    if (employeeExpiration) {
      limits['endDate'] = getUnixTime(new Date(employeeExpiration));
    } else {
      limits['endDate'] = getUnixTime(addYears(locationDate, 2));
    }

    return limits;
  }

  loadDeskReservationsForMonthTask = task({ restartable: true }, async () => {
    const { selectedEmployee } = this.args;
    // calendar shows 6 weeks at a time
    const startDate = getUnixTime(subDays(startOfMonth(this.showingDate), 7));
    const endDate = getUnixTime(addDays(endOfMonth(this.showingDate), 14));
    try {
      const filter = {
        'location-id': this.state.currentLocation?.id,
        'start-date': startDate,
        'end-date': endDate,
        // eslint-disable-next-line ember/no-get
        'user-id': get(selectedEmployee, 'user.id'),
      };
      const reservationParams = {
        filter,
      };
      const reservations = await this.store.query('reservation', reservationParams);
      return reservations.slice();
    } catch (e) {
      this.flashMessages.showFlash('error', 'Error fetching reservations', parseErrorForDisplay(e));
      throw e;
    }
  });

  loadResourceReservationsForMonth = dropTask(
    waitFor(async () => {
      try {
        const { selectedEmployee } = this.args;
        // calendar shows 6 weeks at a time
        const startDate = subDays(startOfMonth(this.showingDate), 7);
        const endDate = addDays(endOfMonth(this.showingDate), 14);

        const queryFilters = {
          'location-id': this.state.currentLocation.id,
          'resource-type-id': '2',
          'start-time': startDate.toISOString(),
          'end-time': endDate.toISOString(),
          'employee-id': selectedEmployee.id,
        };

        const reservationLogEndpoint = urlBuilder.rms.getResourceReservations(queryFilters);
        const response = await this.ajax.request(reservationLogEndpoint, {
          type: 'GET',
          headers: { Accept: 'application/vnd.api+json' },
        });
        const { data } = response as ResourceReservationResponse;
        this.employeeResourceReservationForMonth = data.filter((res) => !res.cancelledAt);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('error', error);
        this.employeeResourceReservationForMonth = [];
      }
    }),
  );

  @action
  hideOnClickOutside(selector: string): void {
    const outsideClickListener = (
      event: MouseEvent & { target: { id?: string; closest: (selector: string) => HTMLElement } },
    ) => {
      const target = event.target.id ? document.getElementById(event.target.id) : event.target;
      if (!target) {
        removeClickListener();
      } else if (target.closest(selector) === null) {
        void this.closeCalendar();
        removeClickListener();
      }
    };

    const removeClickListener = () => {
      if (this.outsideClickListener) {
        document.removeEventListener('click', this.outsideClickListener);
        this.outsideClickListener = null;
      }
    };
    this.showCalendar = true;
    this.outsideClickListener = outsideClickListener as (event: MouseEvent) => void;
    document.addEventListener('click', this.outsideClickListener);
  }

  @action
  async closeCalendar(event?: Event): Promise<void> {
    // Prevent event bubbling to avoid triggering outside click listener
    event?.stopPropagation();

    this.showCalendar = false;
    try {
      if (this.isDesksContext) {
        await this.checkForAvailableDesksTask.perform();
      } else {
        await this.checkForAvailableResourceTask.perform();
      }
    } catch (error: unknown) {
      // Ignore TaskCancelation errors as they're expected when component is destroyed
      if (error && typeof error === 'object' && 'name' in error && error.name !== 'TaskCancelation') {
        throw error;
      }
    }
    this.datesToDisplay = this.args.selectedDates;
  }

  get disableBackButton(): boolean {
    return isPast(endOfMonth(subMonths(this.showingDate, 1)));
  }

  get disableNextMonthButton(): boolean {
    return (
      isAfter(startOfMonth(addMonths(this.showingDate, 1)), fromUnixTime(this.dateLimits.endDate!)) ||
      this.checkForAvailableDesksTask.isRunning
    );
  }

  get isAfterBookingHours(): boolean {
    if (!this.selectedForToday) {
      return false;
    }

    const today = format(subMinutes(this.selectedForToday, this.state.minutesBetweenTimezones()), 'EEEE');
    const currentWorkplaceDay = this.workplaceDays.find((day) => day.dayOfWeek === today);
    const [, partialEnd] = getPartialTimes(startOfDay(new Date()), {
      startTime: currentWorkplaceDay!.startTime,
      endTime: currentWorkplaceDay!.endTime,
    });

    return isAfter(this.state.getOfficeLocationTime(new Date()), this.state.getOfficeLocationTime(partialEnd!));
  }

  get isValidBookingHours(): boolean {
    const { selectedTime } = this.args;
    return selectedTime.startTime.localeCompare(selectedTime.endTime) === -1;
  }

  /**
   * Used for the template
   */
  get partialDayError(): string {
    const { isEditing, selectedEmployee, allDayEnabled, canUsePartialDay } = this.args;
    if (
      (!isEditing || canUsePartialDay) &&
      this.reservationExistsOnSelectedDate &&
      selectedEmployee &&
      !allDayEnabled
    ) {
      return `Selected time overlaps with existing reservation`;
    }
    return '';
  }

  computeReservationErrorForNoDeskAvailability(selectedEmployee: Employee, multidayEnabled: boolean): string | null {
    if (!this.deskAvailabilities.find((desk) => desk.availability === 'false')) {
      return null;
    }

    return `No desks available for ${selectedEmployee.name} on ${multidayEnabled ? 'highlighted dates' : 'this date'}`;
  }

  get hasNoResourceAvailabilitiesForSelectedDate(): boolean {
    return this.args.selectedDates.some((date) => {
      return this.resourceAvailabilitiesByDate[date.toISOString()]?.length === 0;
    });
  }

  get computeReservationErrorForNoResourceAvailability(): string | null {
    const { selectedResourceType, selectedEmployee, multidayEnabled } = this.args;
    if (!this.onEmployeeSelectTask.isRunning && this.hasNoResourceAvailabilitiesForSelectedDate) {
      return `No ${selectedResourceType.code} available for ${selectedEmployee.name} on ${
        multidayEnabled ? 'highlighted dates' : 'this date'
      }`;
    }

    return null;
  }

  /**
   * @returns `string` if there's an error or `null` otherwise. Will always return no error if `canUsePartialDay` is falsey
   */
  computeReservationErrorForPartialDay(): string | null {
    if (!this.isDesksContext) {
      return null;
    }

    const { canUsePartialDay, allDayEnabled } = this.args;

    if (!canUsePartialDay) {
      return null;
    }

    if (this.isInvalidDaySelected) {
      return `Cannot reserve a desk on days with inactive booking hours`;
    } else if (this.isAfterBookingHours) {
      return `Cannot reserve a desk after booking hours for today`;
    } else if (!this.isValidBookingHours && !allDayEnabled) {
      return `There is no overlap between booking hours for the days selected`;
    }

    return null;
  }

  get firstReservationError(): string | undefined {
    return this.reservationErrors[0] ?? this.generalErrors[0];
  }

  /**
   * If the array.length > 0, then there's an error with making a reservation here
   */
  @computed(
    'args.{selectedDates,selectedTime,autoAssignEnabled,multidayEnabled,selectedResourceType,selectedEmployee}',
    'resourceAvailabilitiesByDate',
    'deskAvailabilities',
    'isInvalidDaySelected',
    'isAfterBookingHours',
    'isValidBookingHours',
    'allDayEnabled',
  )
  get reservationErrors(): string[] {
    const { selectedDates, isDbeam } = this.args;

    if (!isNonEmptyArray(selectedDates)) {
      return [`Cannot reserve a desk without selecting a date`];
    }
    const source = isDbeam ? 'employee_log' : 'desk_reservations';
    switch (source) {
      case 'employee_log':
        return this.computeReservationErrorsForEmployeeLogTab();
      case 'desk_reservations':
        return this.computeReservationErrorsForDeskReservationTab();
      default:
        return [];
    }
  }

  get generalErrors(): string[] {
    const errors: string[] = [];
    if (this.canSignIn && !this.selectedForToday) {
      errors.push('Cannot sign in for a date other than today');
    }

    return errors;
  }

  get shouldShowExistingReservationError(): boolean {
    const { isEditing, canUsePartialDay, allDayEnabled, selectedEmployee } = this.args;
    return Boolean(
      (!isEditing || (canUsePartialDay && this.isDesksContext)) &&
        this.reservationExistsOnSelectedDate &&
        selectedEmployee &&
        allDayEnabled,
    );
  }

  computeReservationErrorsForEmployeeLogTab(): string[] {
    const { multidayEnabled, selectedEmployee, autoAssignEnabled } = this.args;

    const partialDayError = this.computeReservationErrorForPartialDay();
    if (partialDayError) {
      return [partialDayError];
    }

    if (!autoAssignEnabled) {
      // we're not attempting to book a desk at all
      return [];
    }

    if (this.shouldShowExistingReservationError) {
      return [
        `${selectedEmployee.name} already has a reservation for ${
          this.args.multidayEnabled ? 'highlighted dates' : 'this date'
        }`,
      ];
    }

    if (!selectedEmployee) {
      return [];
    }

    if (this.isDesksContext) {
      const deskAvailabilityError = this.computeReservationErrorForNoDeskAvailability(
        selectedEmployee,
        multidayEnabled,
      );
      if (deskAvailabilityError) {
        return [deskAvailabilityError];
      }
    } else {
      const resourceAvailabilityError = this.computeReservationErrorForNoResourceAvailability;
      if (resourceAvailabilityError) {
        return [resourceAvailabilityError];
      }
    }

    return [];
  }

  computeReservationErrorsForDeskReservationTab(): string[] {
    const { floorsWithMap, multidayEnabled, selectedEmployee, autoAssignEnabled } = this.args;
    if (!floorsWithMap.length && !autoAssignEnabled) {
      return [`Upload a floor map or enable auto assign desks to create reservations`];
    }

    const partialDayError = this.computeReservationErrorForPartialDay();
    if (partialDayError) {
      return [partialDayError];
    }

    if (this.shouldShowExistingReservationError) {
      return [
        `${selectedEmployee.name} already has a reservation for ${
          this.args.multidayEnabled ? 'highlighted dates' : 'this date'
        }`,
      ];
    }

    if (!selectedEmployee) {
      return [];
    }

    if (this.isDesksContext) {
      const deskAvailabilityError = this.computeReservationErrorForNoDeskAvailability(
        selectedEmployee,
        multidayEnabled,
      );
      if (deskAvailabilityError) {
        return [deskAvailabilityError];
      }
    } else {
      const resourceAvailabilityError = this.computeReservationErrorForNoResourceAvailability;
      if (resourceAvailabilityError) {
        return [resourceAvailabilityError];
      }
    }

    return [];
  }

  hasNoApprovedDocument(employee: Employee): boolean {
    const { selectedDates } = this.args;
    const currentEmployeeLocation = this.getCurrentEmployeeLocation(employee.employeeLocations.content);
    const expiresAt = currentEmployeeLocation?.requiredDocumentApprovalExpiresAt;
    if (expiresAt) {
      return selectedDates.some((date) => isAfter(date, new Date(expiresAt)));
    } else {
      return !currentEmployeeLocation?.hasRequiredDocumentApproval;
    }
  }

  getCurrentEmployeeLocation(employeeLocations: EmployeeLocation[] = []): EmployeeLocation | undefined {
    const currentLocationId = parseInt(this.state.currentLocation?.id ?? '', 10);

    if (!currentLocationId) {
      return;
    }

    return employeeLocations.find((location) => location.locationId === currentLocationId);
  }

  goBackOneMonthTask = task({ drop: true }, async () => {
    this.showingDate = subMonths(this.showingDate, 1);
    await this.loadDeskReservationsForMonthTask.perform();
    await this.loadResourceReservationsForMonth.perform();
  });

  goToNextMonthTask = task({ drop: true }, async () => {
    this.showingDate = addMonths(this.showingDate, 1);
    await this.loadDeskReservationsForMonthTask.perform();
    await this.loadResourceReservationsForMonth.perform();
  });

  setCorrectPartialTimes(): void {
    const { selectedTime } = this.args;
    if (selectedTime.startTime >= selectedTime.endTime) {
      this.setPartialTimes(this.latestStartTime, this.earliestEndTime);
    }
    const startTime =
      selectedTime.startTime > this.latestStartTime && selectedTime.startTime < this.earliestEndTime
        ? selectedTime.startTime
        : this.latestStartTime;
    const endTime =
      selectedTime.endTime < this.earliestEndTime && selectedTime.endTime > this.latestStartTime
        ? selectedTime.endTime
        : this.earliestEndTime;
    this.setPartialTimes(startTime, endTime);
  }

  @action
  removeDate(dateToRemove: string, event: Event): void {
    const { onDateSelect } = this.args;
    onDateSelect(this.state.getOfficeLocationTime(new Date(dateToRemove)));
    this.setCorrectPartialTimes();
    this.datesToDisplay = this.datesToDisplay.filter(
      (date) => !isSameDay(date as Date, this.state.getOfficeLocationTime(new Date(dateToRemove))),
    );
    void this.checkForAvailableDesksTask.perform();
    event.stopPropagation();
  }

  onSelectDateTask = task({ drop: true }, async (selectedDate: moment.Moment) => {
    const { onDateSelect, multidayEnabled } = this.args;
    onDateSelect(selectedDate);
    const epochMilli = selectedDate.unix() * 1000;
    if (this.isDesksContext) {
      this.setCorrectPartialTimes();
      if (!multidayEnabled) {
        this.showCalendar = false;
        this.datesToDisplay = [selectedDate];
        await this.checkForAvailableDesksTask.perform();
      } else if (!isSameMonth(new Date(epochMilli), this.showingDate)) {
        void this.loadDeskReservationsForMonthTask.perform();
      }

      this.showingDate = new Date(epochMilli);
    } else {
      if (!multidayEnabled) {
        this.showCalendar = false;
        this.datesToDisplay = [selectedDate];
        await this.checkForAvailableResourceTask.perform();
      } else if (!isSameMonth(new Date(epochMilli), this.showingDate)) {
        await this.loadResourceReservationsForMonth.perform();
      }
    }
  });

  onEmployeeSelectTask = dropTask(
    waitFor(async (employee: Employee) => {
      const { onEmployeeSelect, onDateSelect, selectedDates } = this.args;
      onEmployeeSelect(employee);
      await this.loadInviteForTodayTask.perform();
      if (getUnixTime(selectedDates[0]!) < this.dateLimits.startDate!) {
        onDateSelect(this.dateLimits.startDate! * 1000);
      }
      this.datesToDisplay = selectedDates;
      if (this.isDesksContext) {
        await this.loadDeskReservationsForMonthTask.perform();
        await this.checkForAvailableDesksTask.perform();
      } else {
        await this.loadResourceReservationsForMonth.perform();
        await this.checkForAvailableResourceTask.perform();
      }

      this.datesToDisplay = this.args.selectedDates;
    }),
  );

  loadEmployeeReservationsTask = dropTask(
    waitFor(async () => {
      const { isEditing, getSelectedEmployeeTask, selectedEmployee } = this.args;
      if (selectedEmployee) {
        return;
      }

      await getSelectedEmployeeTask.perform();
      if (!isEditing) {
        this.setPartialTimes(this.startHourOptions[0]!.value, this.earliestEndTime);
        return;
      }

      await this.loadInviteForTodayTask.perform();
      if (this.isDesksContext) {
        await this.loadDeskReservationsForMonthTask.perform();
        await this.checkForAvailableDesksTask.perform();
      } else {
        await this.loadResourceReservationsForMonth.perform();
        await this.checkForAvailableResourceTask.perform();
      }
    }),
  );

  onEmployeeSearchTask = task(async (term: string) => {
    const { searchTask } = this.args;
    if (term) {
      this.employees = await searchTask.perform(term);
    } else {
      this.employees = this.loadedEmployees;
    }
  });

  // This function is need for the validateUserInput param in the comboBox.
  // The default for the param is a function that returns true.
  @action
  returnFalseValid(): false {
    return false;
  }

  willDestroy(): void {
    super.willDestroy();
    if (this.outsideClickListener) {
      document.removeEventListener('click', this.outsideClickListener);
      this.outsideClickListener = null;
    }
  }

  <template>
    {{#if (and (can "use parking resource") (not @isEmployeeLog))}}
      <UiInputField class="flex-auto min-w-0 min-h-0 mb-5 items-center" @required={{true}} as |Field|>
        <Field.label>What would you like to reserve?</Field.label>
        <PowerSelect
          @selected={{@selectedResourceType}}
          @searchField="name"
          @options={{this.resourceTypeOptions}}
          @onChange={{this.onResourceTypeSelect}}
          @renderInPlace={{true}}
          @disabled={{@isEditing}}
          @triggerClass="!mb-0"
          data-test-resource-type-options-menu
          as |option|
        >
          <div data-test-resource-type-option="{{option.code}}">
            {{option.name}}
          </div>
        </PowerSelect>
      </UiInputField>
    {{/if}}

    <UiInputField class="flex-auto min-w-0 min-h-0 items-center" @required={{true}} as |Field|>
      <Field.label>Employee name</Field.label>
      <ComboBox
        data-test-employee-dropdown
        @activeOptionClass="text-gem-60 bg-gem-10"
        @emptyOption="No employee found"
        @inputClass="input unset-global-margin !rounded-md !max-w-full !mb-5"
        @inputId="employee-input"
        @invalidInputClass="error"
        @invalidLabelClass="error-description"
        @listboxClass="employee-combobox absolute z-10 bg-white overflow-y-auto max-h-44 rounded-lg shadow w-96 -mt-5"
        @onInput={{this.onEmployeeSearchTask.perform}}
        @onSelect={{this.onEmployeeSelectTask.perform}}
        @options={{this.employees}}
        @optionClass="py-2 pl-2 pr-2 outline-none nowrap cursor-pointer trans hover:text-gem-50 hover:bg-gem-5"
        @placeholder="E.g. John Doe"
        @scrollToSelection={{false}}
        @selected={{@selectedEmployee}}
        @shouldDisableOption={{this.reasonEmployeeCannotReserve}}
        @searchField="fullName"
        @selectedOptionClass="text-gem-60 bg-gem-10 hover:text-gem-60 hover:bg-gem-10"
        @type="search"
        @isLoading={{@getSelectedEmployeeTask.isRunning}}
        @validateUserInput={{this.returnFalseValid}}
        @isDisabled={{@isEditing}}
        as |employee|
      >
        {{#if (and (eq employee "loadMore") @hasMorePages)}}
          <div {{on "mouseup" (preventDefault)}}>
            <LoadMore
              class="load-more m-4"
              @hasMorePages={{@hasMorePages}}
              @viewPortSelector=".employee-combobox"
              @action={{@loadMoreTask.perform}}
            />
          </div>
        {{else}}
          <div class="flex justify-between items-center" date-test-employee-option="{{employee.id}}">
            {{#let (this.reasonEmployeeCannotReserve employee) as |reasonDisabled|}}
              <div class="{{if reasonDisabled 'text-carbon-50'}}">
                {{employee.fullName}}
              </div>
              {{#if reasonDisabled}}
                <div class="text-sm text-carbon-50">
                  {{reasonDisabled}}
                </div>
              {{/if}}
            {{/let}}
          </div>
        {{/if}}
      </ComboBox>
    </UiInputField>
    <UiInputField
      class="flex-auto min-w-0 min-h-0 items-center"
      @required={{true}}
      @errors={{this.reservationErrors}}
      as |Field|
    >
      <Field.label>Reservation date</Field.label>
      <div class="date-trigger mb-5 relative outline-none" {{didInsert this.loadEmployeeReservationsTask.perform}}>
        <div class="flex items-center justify-between">
          <UiCard
            class="cursor-pointer border-solid px-4 pt-2 pb-0 w-full min-h-[40px]
              {{if this.reservationErrors.length 'border-papaya-60' 'border-carbon-20'}}
              {{if (or (not @selectedEmployee) this.onEmployeeSelectTask.isRunning) 'pointer-events-none bg-carbon-5'}}"
            {{on "click" (fn this.hideOnClickOutside ".date-trigger")}}
            data-test-date-trigger
          >
            {{#if @multidayEnabled}}
              <div class="flex flex-wrap max-h-[160px] overflow-y-auto">
                {{#each this.selectedDatesLocationTime as |date|}}
                  <div
                    class="{{if (call (fn this.dateHasError date)) 'bg-papaya-5 text-papaya-30' 'bg-carbon-5'}}
                      rounded-lg w-28 px-2 py-1 mr-3 mb-2 whitespace-nowrap"
                    data-test-selected-date
                  >
                    {{#if (notEq this.selectedDatesLocationTime.length 1)}}
                      <span {{on "click" (fn this.removeDate date)}}>x</span>
                    {{/if}}
                    {{date}}
                  </div>
                {{/each}}
              </div>
            {{else if this.selectedDatesLocationTime.length}}
              <div data-test-selected-date class="mb-2">{{this.firstSelectedDatesLocationTime}}</div>
            {{/if}}
          </UiCard>
          {{#if (and @canUsePartialDay this.isDesksContext)}}
            <label class="w-[110px] ml-3">
              <label class="toggle--tiny" data-test-all-day-toggle>
                <input
                  type="checkbox"
                  checked={{@allDayEnabled}}
                  disabled={{or (not @selectedEmployee) this.onEmployeeSelectTask.isRunning this.isAfterBookingHours}}
                  {{on "click" this.onAllDayToggleClick}}
                />
                <span></span>
              </label>
              <span class="text-carbon-50 whitespace-nowrap">All day</span>
            </label>
          {{/if}}
        </div>
        {{#if this.showCalendar}}
          <div class="calendar-popover bg-white" style="top: 56px;">
            <div class="dashboardCalendar">
              <div class="dashboardCalendar-header mx-2">
                <div class="header">
                  <UiButton
                    data-test-go-back-month
                    role="button"
                    class="relative float-left previous btn-reset"
                    disabled={{this.disableBackButton}}
                    {{on "click" this.goBackOneMonthTask.perform}}
                  />
                  <span>{{dateFormat this.showingDate "MMMM yyyy"}}</span>
                  <UiButton
                    data-test-go-next-month
                    role="button"
                    class="relative float-right next btn-reset"
                    disabled={{this.disableNextMonthButton}}
                    {{on "click" this.goToNextMonthTask.perform}}
                  />
                  <div class="clear"></div>
                </div>
              </div>
              {{#if this.loadDeskReservationsForMonthTask.isRunning}}
                <div class="bg-white w-full absolute rounded-lg leaflet-loader h-64">
                  <LoadingSpinnerPulse />
                </div>
              {{else}}
                {{#if @canUsePartialDay}}
                  <CalendarGrid
                    @currentMonth={{this.currentMonth}}
                    @selectedDates={{@selectedDates}}
                    @selectDate={{this.onSelectDateTask.perform}}
                    @daysToCustomize={{this.daysToCustomize}}
                    @activeDays={{this.activeDays}}
                    @dateLimits={{this.dateLimits}}
                    @respectSchedulingLimits={{true}}
                  />
                {{else}}
                  <CalendarGrid
                    @currentMonth={{this.currentMonth}}
                    @selectedDates={{@selectedDates}}
                    @selectDate={{this.onSelectDateTask.perform}}
                    @daysToCustomize={{this.daysToCustomize}}
                    @dateLimits={{this.dateLimits}}
                    @respectSchedulingLimits={{true}}
                  />
                {{/if}}
                {{#unless @isEditing}}
                  <div class="flex justify-between items-center m-4">
                    <label>
                      <label class="toggle--tiny" data-test-enable-multiday-toggle>
                        <input type="checkbox" checked={{@multidayEnabled}} {{on "click" this.onMultiSelectClick}} />
                        <span></span>
                      </label>
                      <span class="text-carbon-50">Select Multiple</span>
                    </label>
                    {{#if @multidayEnabled}}
                      <button
                        data-test-done-button
                        type="button"
                        class="btn btn--small btn--brand"
                        {{on "click" this.closeCalendar}}
                      >
                        Done
                      </button>
                    {{/if}}
                  </div>
                {{/unless}}
              {{/if}}
            </div>
          </div>
        {{/if}}
      </div>
    </UiInputField>
    {{#if (and (not @allDayEnabled) @canUsePartialDay)}}
      <div class="flex">
        <div class="w-1/2 mr-3">
          <div class="text-carbon-50">Start time</div>
          <PowerSelect
            @options={{this.startHourOptions}}
            @onChange={{fn this.onPartialTimeChange "startTime"}}
            @triggerClass="!mb-0 {{if this.partialDayError '!border-papaya-60'}}"
            @placeholder={{this.startTimePlaceholder}}
            @selected={{findBy "value" @selectedTime.startTime this.startHourOptions}}
            @renderInPlace={{true}}
            data-test-delegated-start-time
            as |option|
          >
            {{option.label}}
          </PowerSelect>
        </div>
        <div class="w-1/2">
          <div class="text-carbon-50">End time</div>
          <PowerSelect
            @options={{this.endHourOptions}}
            @onChange={{fn this.onPartialTimeChange "endTime"}}
            @triggerClass="!mb-0 {{if this.partialDayError '!border-papaya-60'}}"
            @placeholder={{this.endTimePlaceholder}}
            @selected={{findBy "value" @selectedTime.endTime this.endHourOptions}}
            @renderInPlace={{true}}
            data-test-delegated-end-time
            as |option|
          >
            {{option.label}}
          </PowerSelect>
        </div>
      </div>
      {{#if this.partialDayError}}
        <div data-test-partial-day-error class="text-papaya-60 text-xs mt-1">{{this.partialDayError}}</div>
      {{/if}}
    {{/if}}
    <div class="flex items-center mt-6 justify-between">
      {{#if this.canAutoAssignDesks}}
        <label class="flex items-center">
          <input
            type="checkbox"
            class="mx-1"
            {{on "click" @onAutoAssignDesksClick}}
            checked={{@autoAssignEnabled}}
            disabled={{this.disableAutoDeskAssignment}}
            data-test-auto-assign-checkbox
          />
          <div data-test-auto-assign-label class="pt-[3px]">Auto assign {{@selectedResourceType.code}}</div>
        </label>
      {{else}}
        <div></div>
      {{/if}}
      <div class="flex">
        {{#if (and @isEditing (can "delete reservations"))}}
          <UiButton
            data-test-delete-button
            class="btn btn--small btn--outlineFill mr-3"
            {{on "click" (fn @setCurrentPage "delete")}}
            disabled={{not @selectedEmployee}}
          >
            Delete
          </UiButton>
        {{/if}}
        <div>
          <UiButton
            data-test-next-button
            class="btn btn--small {{if this.canSignIn 'btn--outlineFill' 'btn--brand'}}"
            {{on "click" (fn this.onNextButtonClick false)}}
            disabled={{this.disableNextButton}}
          >
            {{this.nextButtonText}}
          </UiButton>

          {{#if this.canSignIn}}
            <UiButton
              data-test-next-button
              class="btn btn--small btn--brand"
              {{on "click" (fn this.onNextButtonClick true)}}
              disabled={{or this.disableNextButton (not this.selectedForToday)}}
            >
              Schedule and sign in
            </UiButton>
          {{/if}}

          {{#if this.firstReservationError}}
            <EmberTooltip @side="bottom" @text={{this.firstReservationError}} />
          {{/if}}
        </div>
      </div>
    </div>
  </template>
}
